import { ContentType, PlaceholderName } from '@ocp-zmarta/content-lib'
import { CONTENT_SOURCE } from '../constants/content'
import { isRichTextField } from '../helper'
import themeToProduct from './theme-to-product'
import { localizedDateFormat } from '../date'

export default {
  [ContentType.ArticlePreview]: {
    componentType: 'ZgArticlePreviewSection',
    slots: {
      subTitle: 'sub-title'
    },
    transform: (fields, _source, site) => {
      fields.articles = fields.articles?.map((article) => ({
        ...article,
        publishedAt: localizedDateFormat(article.publishedAt, site),
        hero: article.hero
          ? {
              ...article.hero,
              src: article.hero.src ? `${article.hero.src}?fm=webp&fit=scale&q=80&w=800&h=493` : undefined
            }
          : undefined
      })) ?? []
    }
  },
  [ContentType.Button]: {
    componentType: 'ZgButtonLink',
    transform: fields => {
      fields.href = fields.link?.href
      fields.buttonProps = {
        icons: { right: fields.icon }
      }

      delete fields.link
      delete fields.icon
    }
  },
  [ContentType.Card]: {
    componentType: 'ZgCard',
    slots: {
      textContent: 'text-content'
    },
    transform: fields => {
      fields.textContent = fields.text

      delete fields.text
    }
  },
  [ContentType.CardGrid]: {
    componentType: 'ZgCardSection',
    slots: fields => {
      const obj = {
        subTitle: 'sub-title'
      }

      fields.items?.forEach((card, index) => {
        obj[`textContent${index}`] = `text-content-${index}`
      })

      return obj
    },
    transform: fields => {
      fields.cards?.forEach((item, index) => {
        fields[`textContent${index}`] = item.text

        delete fields.cards[index]?.textContent
      })

      fields.items = fields.cards

      delete fields.cards
    }
  },
  [ContentType.Chart]: {
    componentType: 'ZgCharts',
    transform: fields => {
      fields.chartData = fields.data
      fields.chartType = fields.type
    }

  },
  [ContentType.CrossSell]: {
    componentType: 'ZgCrossSell',
    transform: (fields, source) => {
      if (source === CONTENT_SOURCE.FIELD) {
        fields.fullWidth = true
      }
    }
  },
  [ContentType.CrossSellSection]: {
    componentType: 'ZgCrossSellSection',
    slots: {
      subTitle: 'sub-title'
    },
    transform: fields => {
      fields.items = fields.items?.map(item => ({
        ...item,
        product: item.product || themeToProduct[item.theme] || item.theme // todo: remove transform once item.product has been added to contentful
      }))
    }
  },
  [ContentType.DividedContent]: {
    componentType: 'ZgDividedContent',
    slots: {
      title: 'title',
      left: 'left',
      right: 'right'
    }
  },
  [ContentType.FaqSection]: {
    componentType: (_, source) => source === CONTENT_SOURCE.RICHTEXT ? 'ZgFaq' : 'ZgFaqSection',
    slots: {
      subTitle: 'sub-title'
    },
    transform: (fields, source) => {
      fields[source === CONTENT_SOURCE.RICHTEXT ? 'faqs' : 'items'] = fields.faqs?.map(({ question, answer: [answer] }) => ({
        question,
        answer: answer.value
      }))

      fields.showStructuredData = false
    }
  },
  [ContentType.Fragment]: {
    componentType: 'FragmentComponent',
    transform: fields => {
      fields.ssr = ['ssr', 'server', 'client'].includes(fields.type)
      fields.ssi = ['ssi'].includes(fields.type)
      fields.url = fields.path
    }
  },
  [ContentType.MediaSection]: {
    componentType: ({ mediaType }) => mediaType === 'Video' ? 'ZgVideoSection' : 'ZgLogoCardSection',
    slots: {
      subTitle: 'sub-title'
    },
    transform: fields => {
      if (fields.componentType === 'ZgLogoCardSection') {
        fields.items = (fields.items && fields.items?.map(item => ({ ...item, image: item.media }))) || []
      }
    }
  },
  [ContentType.LinkList]: {
    transform: fields => {
      fields.items = fields.links
      delete fields.links
    }
  },
  [ContentType.PageLinks]: {
    componentType: 'ZgPageLinkSection',
    slots: {
      subTitle: 'sub-title'
    },
    transform: fields => {
      fields.items = fields.items?.map(item => ({
        ...item,
        href: item.links?.href,
        product: item.product || themeToProduct[item.theme] || item.theme // todo: remove everything after item.product once it has been added to contentful
      }))
    }
  },
  [ContentType.Partner]: {
    transform: fields => {
      fields.name = fields.title
      fields.orgNo = fields.orgNumber
      fields.orgNoLabel = fields.orgNumberLabel
      fields.phoneNo = fields.phoneNumber

      if (fields.image?.src) {
        fields.logo = {
          src: fields.image.src,
          title: fields.title
        }
      }

      if (isRichTextField(fields.description)) {
        fields.descriptionSlot = fields.description
        delete fields.description
      }

      delete fields.title
      delete fields.orgNumber
      delete fields.orgNumberLabel
      delete fields.phoneNumber
    }
  },
  [ContentType.Partners]: {
    componentType: 'ZgLogoCardSection',
    slots: {
      subTitle: 'sub-title'
    },
    transform: fields => {
      fields.items = fields.partners
      delete fields.partners
    }
  },
  [ContentType.Placeholder]: {
    componentType: fields => {
      const typeMap = {
        [PlaceholderName.PressReleases]: 'ZgArticlePreviewSection',
        [PlaceholderName.Testimonial]: 'ZgTestimonialSection',
        [PlaceholderName.Tips]: 'ZgChipsListSection',
        [PlaceholderName.Custom]: ['ourBrands', 'selectedPartners'].includes(fields.json?.name)
          ? 'ZgLogoCardSection'
          : null,
        [PlaceholderName.CrossSellSpecial]: 'ZgCrossSellItemSection',
        [PlaceholderName.LinkList]: 'ZgLinkListSection',
        [PlaceholderName.JumpLinks]: 'ZgJumpLinkSection'
      }

      return typeMap[fields.placeholderName] || 'ZgTextSection'
    },
    slots: {
      subTitle: 'sub-title'
    },
    transform: (fields, _source, site) => {
      switch (fields.placeholderName) {
        case PlaceholderName.Custom: {
          if (fields.json?.name === 'ourBrands') {
            fields.title = fields.json?.h3
            fields.items = fields.json?.list?.map(({ alt, src, url }) => ({
              image: {
                title: alt,
                src,
                alt
              },
              link: {
                target: '_blank',
                href: url
              }
            })) || []
          } else if (fields.json?.name === 'selectedPartners') {
            fields.title = fields.json?.h3
            fields.items = fields.json?.list?.map(({ alt, src }) => ({
              image: {
                title: alt,
                src
              }
            })) || []
          }

          break
        }
        case PlaceholderName.PressReleases: {
          fields.articles = fields.items?.map(item => ({
            lazyLoad: true,
            publishedAt: localizedDateFormat(item.PublishDate, site),
            headline: item.headline,
            preamble: item.preamble,
            hero: item.hero,
            link: {
              nofollow: item.link?.nofollow,
              path: item.link?.href,
              text: item.link?.text
            }
          })) || []

          delete fields.items

          break
        }

        case PlaceholderName.Testimonial: {
          const { link, testimonials, trustpilotData } = fields.data || {}

          fields.readMoreLink = {
            url: link?.href,
            text: link?.text,
            newTab: true
          }
          fields.testimonials = testimonials
          fields.trustpilotData = trustpilotData

          delete fields.data

          break
        }

        case PlaceholderName.Tips: {
          fields.items = fields.items?.[0]?.links

          break
        }

        case PlaceholderName.CrossSellSpecial: {
          fields.items = fields.items?.[0]?.links
          fields.showMoreDefault = fields?.json?.showMoreDefault ?? false

          delete fields.json

          break
        }

        case PlaceholderName.LinkList: {
          fields.items = fields.items?.map(item => ({
            ...item,
            items: item.links
          }))

          break
        }

        default: {
          break
        }
      }
    }
  },
  [ContentType.Quote]: {
    componentType: fields => fields.inlineQuote ? 'ZgInlineQuote' : 'ZgQuotedBySection',
    transform: fields => {
      fields.name = `${fields.author?.firstName} ${fields.author?.surname}`
      fields.bioLink = fields.link?.href
      fields.jobTitle = fields.author?.jobTitle
      fields.image = {
        title: fields.author?.profilePicture?.title,
        src: fields.author?.profilePicture?.src,
        alt: fields.author?.profilePicture?.alt
      }
      fields.text = fields.quote
      fields.background = fields.backgroundColor
      fields.trustpilotData = fields.testimonials?.data?.trustpilotData
      fields.disclaimer = fields.testimonialsDisclaimer
    }
  },
  [ContentType.ReviewedBy]: {
    componentType: 'ZgReviewedBySection',
    slots: {
      bio: 'default'
    },
    transform: (fields, _source, site) => {
      fields.image = {
        title: fields.author?.profilePicture?.title,
        src: fields.author?.profilePicture?.src,
        alt: fields.author?.profilePicture?.alt
      }
      fields.name = `${fields.author?.firstName} ${fields.author?.surname}`
      fields.jobTitle = fields.author?.jobTitle
      fields.bioLink = fields.link?.href
      fields.bio = fields.author?.bio
      fields.date = localizedDateFormat(fields.updatedAt, site)

      delete fields.author
      delete fields.createdAt
      delete fields.link
    }
  },
  [ContentType.SoloSection]: {
    componentType: 'ZgItemSection',
    slots: {
      subTitle: 'sub-title'
    },
    transform: fields => {
      for (const item of fields.items) {
        if (item.image) {
          item.imageSrc = item.image?.src
          item.imageTitle = item.image?.alt || item.image?.title
          delete item.image
        }
      }
    }
  },
  [ContentType.Teaser]: {
    componentType: 'ZgTeaserSection',
    slots: {
      text: 'text'
    }
  },
  [ContentType.TextContent]: {
    componentType: 'ZgTextSection',
    slots: {
      subTitle: 'sub-title',
      body: 'body'
    }
  },
  [ContentType.Tips]: {
    componentType: 'ZgTipsSection',
    slots: {
      subTitle: 'sub-title'
    },
    transform: fields => {
      fields.textContent = fields.text

      delete fields.text
    }
  }
}
